import * as React from 'react';
import {classNames} from '@shopify/react-utilities/styles';
import {focusFirstFocusableNode} from '@shopify/javascript-utilities/focus';
import {NOOP} from 'components/UIKit/utilities';
import styles from './Dropdown.scss';

interface DropdownProps {
  align?: 'left' | 'right';
  active: boolean;
  ariaLabel?: string;
  trigger: string | React.ReactNode;
  wrapperComponent?: string | React.ComponentClass;
  children: React.ReactNode;
  onOpen: () => any;
  onClose: () => any;
}

const HOVER_GRACE_PERIOD = 200;

export class Dropdown extends React.PureComponent<DropdownProps> {
  hoverTimer?: number = undefined;
  container: React.RefObject<HTMLDivElement>;
  dropdown: React.RefObject<HTMLDivElement>;
  trigger: React.RefObject<HTMLButtonElement>;

  constructor(props) {
    super(props);
    this.container = React.createRef();
    this.dropdown = React.createRef();
    this.trigger = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.active &&
      this.props.active &&
      this.dropdown.current &&
      // Only focus the first element if the dropdown is accessed via keyboard navigation
      document.activeElement === this.trigger.current
    ) {
      focusFirstFocusableNode(this.dropdown.current);
    }
  }

  componentDidMount() {
    window.addEventListener('focusin', this.handleFocusin);
  }

  componentWillUnmount() {
    window.removeEventListener('focusin', this.handleFocusin);
  }

  resetHoverTimer = () => {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = undefined;
  };

  doClose = () => {
    const closed = !this.props.active;
    const {onClose} = this.props;

    if (closed) {
      return;
    }

    onClose();
    this.resetHoverTimer();
  };

  doOpen = () => {
    const {active, onOpen = NOOP} = this.props;
    if (active) {
      return;
    }

    onOpen();
  };

  handleFocusin = (event) => {
    const closed = !this.props.active;
    if (closed) {
      return;
    }

    if (this.container.current && !this.container.current.contains(event.target)) {
      this.doClose();
    }
  };

  handleCurtainMouseMove = (event) => {
    event.stopPropagation();

    if (this.hoverTimer) {
      return;
    }

    if (window) {
      this.hoverTimer = window.setTimeout(this.doClose, HOVER_GRACE_PERIOD);
    }
  };

  handleContainerMouseMove = () => {
    if (this.hoverTimer) {
      this.resetHoverTimer();
    }
  };

  handleContainerKeydown = (event) => {
    if (event.key === 'Escape') {
      this.doClose();
    } else {
      return;
    }

    if (this.trigger.current) {
      this.trigger.current.focus();
    }
  };

  render() {
    const {
      align = 'left',
      active,
      ariaLabel,
      trigger,
      wrapperComponent = 'div',
      children,
    } = this.props;
    const Wrapper = wrapperComponent;

    return (
      <Wrapper
        className={styles.wrapper}
        onKeyDown={this.handleContainerKeydown}
        onMouseMove={this.handleContainerMouseMove}
        ref={this.container}
      >
        <button
          className={styles.trigger}
          type="button"
          onClick={this.doOpen}
          onMouseMove={this.doOpen}
          aria-haspopup
          aria-expanded={active}
          aria-label={ariaLabel}
          ref={this.trigger}
        >
          {trigger}
        </button>
        {active && <div className={styles.curtain} onMouseMove={this.handleCurtainMouseMove} />}
        <div
          className={classNames(styles.dropdown, active && styles.active, styles[align])}
          ref={this.dropdown}
          role="menu"
          aria-label="submenu"
          aria-hidden={!active}
        >
          {children}
        </div>
      </Wrapper>
    );
  }
}
